/* =============================================================================
   VIEWS / MENU-PICKER-ACTIONS — Event-Handler für inline-onclick (mp*)
   =============================================================================
   S69 REF-4 Cut 3/3: aus menu-picker.js (1.354 LOC) extrahiert.

   Alle `mp*`-Funktionen, die State mutieren + Re-Render triggern. Sie werden
   aus String-onclick-Handlern im Render-Layer aufgerufen (`onclick="mpAddItem('m_burg_01')"`).

   Cross-Module-Bindings via ESM live-bindings:
     - `_state` aus menu-picker.js (named export, Object-Mutation funktioniert
       über live binding, kein Re-Assignment)
     - `findItem`, `findLine`, `newLineId`, `optionsByGroup` als state-aware
       Helper aus Hauptmodul
     - `softRefresh`, `renderAll` Re-Render-Triggers aus Hauptmodul
     - `recalcLine` direkt aus menu-picker-helpers.js (pure)
============================================================================ */

import { showToast } from '../utils/helpers.js';
import { recalcLine, resolveExtrasGroup, buildCartRequestFromLine } from './menu-picker-helpers.js';
import { rulesForDishGroup, selectionRuleError, extraToggleAction } from '../core/menu-configurator.js';
import {
  _state,
  findItem,
  findLine,
  newLineId,
  optionsByGroup,
  getMenu,
  softRefresh,
  renderAll,
  closeSoloConfigurator
} from './menu-picker.js';

export function mpSetDietFilter(v) {
  _state.dietFilter = v;
  renderAll();
}

export function mpToggleCategory(cat) {
  if (_state.openCategories.has(cat)) _state.openCategories.delete(cat);
  else _state.openCategories.add(cat);
  renderAll();
}

/** Browse-Klick: öffnet das Konfigurations-Modal mit einer Draft-Zeile,
 *  in der der Gast Brötchen/Patty/Streichungen/Extras/Beilage/Soßen pflegt.
 *  Die Draft wird erst durch `mpConfirmDraft` in den Warenkorb übernommen
 *  und durch `mpCancelDraft` verworfen. Bis dahin teilt sie sich alle
 *  Konfigurations-Handler (mpSelectBun, mpStrikeIngredient, …) mit den
 *  Warenkorb-Zeilen — auflösen über `findLine('__draft__')`. */
export function mpAddItem(id) {
  const item = findItem(id);
  if (!item) {
    showToast('Gericht nicht gefunden.', 'error');
    return;
  }
  const meta = item.meta || {};
  const draft = {
    lineId: '__draft__',
    ref: item.id,
    name: item.name,
    category: item.category,
    qty: 1,
    unitPrice: Number(item.price) || 0,
    bun: null,
    patty: null,
    dressing: null,
    milk: null,
    removedIngredients: [],
    extras: [],
    sauces: [],
    // S229 T1 — Mehrfach-Beilagen: sides[] statt Single-side. sideSauces bleibt
    // EINE Liste pro Position (Dips gelten der Position, nicht je Beilage).
    sides: [],
    sideSauces: [],
    // S196 (Jonas) — Positions-Kommentar für Küche/Theke. NUR Cockpit-Kontext
    // (Operator) pflegt das im Konfigurator; wird beim Bestellen mit der Runde
    // an KDS/Theke übergeben (order_items.notes). Gast nutzt Warenkorb-Kommentar.
    note: ''
  };
  // Default-Brötchen bei Burgern setzen (das erste mit isDefault, sonst das erste).
  if (meta.isBurger && meta.bunGroup) {
    const buns = optionsByGroup(meta.bunGroup);
    const def = buns.find(b => b.meta && b.meta.isDefault) || buns[0];
    if (def) draft.bun = { id: def.id, name: def.name, extra: (def.meta && Number(def.meta.extraPrice)) || 0 };
  }
  recalcLine(draft);
  _state.draftLine = draft;
  softRefresh();
}

/** Modal-Confirm: validiert Pflicht-Optionen. Im NEU-Modus pusht die Draft
 *  als neue Bestellzeile in den Warenkorb; im EDIT-Modus ersetzt sie die
 *  ursprüngliche Zeile in-place (gleiche lineId, gleiche Position). */
export function mpConfirmDraft() {
  if (!_state.draftLine) return;
  const draft = _state.draftLine;
  const item = findItem(draft.ref);
  const meta = (item && item.meta) || {};
  if (meta.isBowl && meta.requiresPatty && !draft.patty) {
    showToast('Bitte ein Patty wählen.', 'error');
    return;
  }
  if (meta.isBowl && meta.requiresDressing && !draft.dressing) {
    showToast('Bitte ein Dressing wählen.', 'error');
    return;
  }
  if (meta.isBurger && !draft.bun) {
    showToast('Bitte ein Brötchen wählen.', 'error');
    return;
  }
  // F7b: Milchgetränke brauchen eine Milch-Auswahl (Heuristik + meta-Flag).
  const itemName = String((item && item.name) || '').toLowerCase();
  const requiresMilk = (meta && meta.allowMilkChoice) ||
                       /latte|cappuccino|kakao|milchkaffee|chai|matcha|flat white|cortado|macchiato/i.test(itemName);
  if (requiresMilk && !draft.milk) {
    showToast('Bitte Milch wählen (Kuh / Hafer / Mandel).', 'error');
    return;
  }
  // S193 — Optionssatz-Regeln der Extras vom Gericht (Pflicht/min/max) prüfen.
  // Fallback (keine Regel / un-migriert) = keine Extras-Pflicht (heutiges Verhalten).
  const extrasRules = extrasRulesForLineItem(item);
  if (extrasRules) {
    const err = selectionRuleError((draft.extras || []).length, extrasRules, 'Extras');
    if (err) { showToast(err, 'error'); return; }
  }

  // S197 Cut b2 — Solo-Re-Edit einer persistierten Position: NICHT _state.lines
  // anfassen, sondern den Callback (→ update_order_item RPC) rufen. Validierungen
  // oben (Bun/Patty/Dressing/Milch/Extras-Regeln) laufen identisch mit.
  if (_state.soloEdit) {
    recalcLine(draft);
    const onConfirm = _state.soloEdit.onConfirm;
    const finalDraft = { ...draft };
    closeSoloConfigurator();          // Modal schließen + soloEdit/draftLine clearen
    if (typeof onConfirm === 'function') {
      Promise.resolve(onConfirm(finalDraft)).catch(err => console.error('[soloConfirm]', err));
    }
    return;
  }

  if (draft._isEdit && draft._originalLineId) {
    // Edit-Modus: ursprüngliche Zeile durch die Draft ersetzen.
    const origId = draft._originalLineId;
    const idx = _state.lines.findIndex(l => l.lineId === origId);
    if (idx >= 0) {
      const final = { ...draft, lineId: origId };
      delete final._isEdit;
      delete final._originalLineId;
      delete final._pattySwapOpen;
      recalcLine(final);
      _state.lines[idx] = final;
    }
    _state.draftLine = null;
    softRefresh();
    showToast('Position aktualisiert', 'success');
    return;
  }

  // S244 Cut B — Tisch-Pfad: der gemeinsame Warenkorb liegt auf dem SERVER.
  // Die Zeile wandert deshalb nicht in `_state.lines`, sondern direkt an
  // `table_cart_add`; der Picker bleibt leer. Zwei Warenkörbe nebeneinander
  // (lokal + Tisch) wären genau das Dual-System, das der Cut vermeiden soll.
  if (_state.onLineConfirmed) {
    delete draft._pattySwapOpen;
    const req = buildCartRequestFromLine(draft);
    _state.draftLine = null;
    softRefresh();
    Promise.resolve(_state.onLineConfirmed(req)).catch(e => console.error('[onLineConfirmed]', e));
    return;
  }

  // NEU-Modus: Echte lineId zuweisen und in den Warenkorb übernehmen.
  draft.lineId = newLineId();
  delete draft._pattySwapOpen;
  recalcLine(draft);
  _state.lines.push(draft);
  _state.draftLine = null;
  softRefresh();
  showToast(`${draft.name} zum Warenkorb hinzugefügt`, 'success');
}

/** Öffnet das Konfigurations-Modal für eine bereits bestätigte Bestellzeile.
 *  Wir deep-kopieren die Zeile in die Draft (damit Abbrechen den Original-
 *  Zustand erhält) und markieren sie als Edit. Confirm ersetzt die
 *  Original-Zeile in-place; Cancel verwirft die Draft. */
export function mpEditLine(lineId) {
  const orig = _state.lines.find(l => l.lineId === lineId);
  if (!orig) return;
  const draft = JSON.parse(JSON.stringify(orig));
  draft._isEdit = true;
  draft._originalLineId = lineId;
  draft.lineId = '__draft__';
  delete draft._pattySwapOpen;
  _state.draftLine = draft;
  softRefresh();
}

/** Modal-Abbruch: Draft verwerfen, Modal schließen. Nichts landet im Warenkorb. */
export function mpCancelDraft() {
  if (!_state.draftLine) return;
  // S197 Cut b2 — Solo-Re-Edit: Host abbauen + onClose (Warenkorb neu rendern).
  if (_state.soloEdit) {
    const onClose = _state.soloEdit.onClose;
    closeSoloConfigurator();
    if (typeof onClose === 'function') onClose();
    return;
  }
  _state.draftLine = null;
  softRefresh();
}

export function mpRemoveLine(lineId) {
  const idx = _state.lines.findIndex(l => l.lineId === lineId);
  if (idx >= 0) _state.lines.splice(idx, 1);
  softRefresh();
}

export function mpChangeQty(lineId, delta) {
  const line = findLine(lineId);
  if (!line) return;
  line.qty = Math.max(1, (line.qty || 1) + delta);
  recalcLine(line);
  softRefresh();
}

/** S196 (Jonas) — Positions-Kommentar für Küche/Theke setzen (Cockpit-only,
 *  Feld nur im Operator-Konfigurator sichtbar). KEIN Re-Render (Fokus/Cursor im
 *  Textfeld erhalten); der Wert steht schon im DOM, wir spiegeln ihn nur in den
 *  State. Wird via mpConfirmDraft/mpEditLine mit der Line übernommen. */
export function mpSetLineNote(lineId, value) {
  const line = findLine(lineId);
  if (!line) return;
  line.note = value || '';
}

export function mpSelectBun(lineId, bunId) {
  const line = findLine(lineId);
  if (!line) return;
  const bun = findItem(bunId);
  if (!bun) return;
  line.bun = { id: bun.id, name: bun.name, extra: (bun.meta && Number(bun.meta.extraPrice)) || 0 };
  recalcLine(line);
  softRefresh();
}

export function mpSelectPatty(lineId, pattyId) {
  const line = findLine(lineId);
  if (!line) return;
  if (!pattyId) { line.patty = null; softRefresh(); return; }
  const p = findItem(pattyId);
  if (!p) return;
  line.patty = { id: p.id, name: p.name };
  softRefresh();
}

export function mpSelectDressing(lineId, dressingId) {
  const line = findLine(lineId);
  if (!line) return;
  if (!dressingId) { line.dressing = null; softRefresh(); return; }
  const d = findItem(dressingId);
  if (!d) return;
  line.dressing = { id: d.id, name: d.name };
  softRefresh();
}

/** Milch-Auswahl bei Milchgetränken (F7b, Session 20). Klick auf den aktiven
 *  Chip de-selektiert ihn — sonst wird der Wert gesetzt. Optionen sind fest
 *  vorgegeben (Kuh / Hafer / Mandel), keine DB-Tabelle nötig. */
export function mpSelectMilk(lineId, milkId) {
  const line = findLine(lineId);
  if (!line) return;
  const MILK_NAMES = {
    milk_kuh:    'Kuhmilch',
    milk_hafer:  'Hafermilch',
    milk_mandel: 'Mandelmilch'
  };
  if (line.milk && line.milk.id === milkId) {
    // Toggle off
    line.milk = null;
  } else {
    line.milk = { id: milkId, name: MILK_NAMES[milkId] || milkId };
  }
  softRefresh();
}

export function mpStrikeIngredient(lineId, ingredient) {
  const line = findLine(lineId);
  if (!line) return;
  line.removedIngredients = line.removedIngredients || [];
  const idx = line.removedIngredients.indexOf(ingredient);
  if (idx >= 0) line.removedIngredients.splice(idx, 1);
  else line.removedIngredients.push(ingredient);
  softRefresh();
}

/** Spezialfall: User streicht das Patty komplett (kein Fleisch / vegan-Wunsch).
 *  Wir merken uns das als pseudo-Zutat '__patty__' im removedIngredients-Array. */
export function mpTogglePatty(lineId) {
  const line = findLine(lineId);
  if (!line) return;
  line.removedIngredients = line.removedIngredients || [];
  const idx = line.removedIngredients.indexOf('__patty__');
  if (idx >= 0) {
    line.removedIngredients.splice(idx, 1);
  } else {
    line.removedIngredients.push('__patty__');
    // Wenn das Patty gestrichen wird, öffnen wir automatisch das Tausch-Panel,
    // damit der Gast unmittelbar eine Alternative sieht.
    line._pattySwapOpen = true;
  }
  softRefresh();
}

export function mpOpenPattySwap(lineId) {
  const line = findLine(lineId);
  if (!line) return;
  line._pattySwapOpen = true;
  softRefresh();
}

export function mpClosePattySwap(lineId) {
  const line = findLine(lineId);
  if (!line) return;
  delete line._pattySwapOpen;
  softRefresh();
}

export function mpSwapPatty(lineId, pattyId) {
  const line = findLine(lineId);
  if (!line) return;
  const p = findItem(pattyId);
  if (!p) return;
  line.patty = { id: p.id, name: p.name };
  // Streichung aufheben, wenn vorher gestrichen war.
  if (Array.isArray(line.removedIngredients)) {
    const idx = line.removedIngredients.indexOf('__patty__');
    if (idx >= 0) line.removedIngredients.splice(idx, 1);
  }
  delete line._pattySwapOpen;
  softRefresh();
}

/** S193 — aufgelöste Extras-Regeln (dish-first, Fallback Heuristik) für das Item
 *  einer Bestellzeile; null wenn das Gericht keine Extras-Gruppe hat. */
function extrasRulesForLineItem(item) {
  const group = resolveExtrasGroup(getMenu(), item);
  if (!group) return null;
  return rulesForDishGroup((item && item.meta) || {}, group, { role: 'extras', label: 'Extras' });
}

export function mpToggleExtra(lineId, extraId) {
  const line = findLine(lineId);
  if (!line) return;
  line.extras = line.extras || [];
  const idx = line.extras.findIndex(e => e.id === extraId);
  // S193-Fix3 — Optionssatz-Regeln vom Gericht über die pure Entscheidung
  // extraToggleAction: multi:true → Mehrfach bis max (dann Block+Toast);
  // multi:false → Einfachauswahl (ersetzt); keine Regel → Mehrfach unbegrenzt.
  const rules = extrasRulesForLineItem(findItem(line.ref));
  const action = extraToggleAction(idx >= 0, line.extras.length, rules);
  if (action === 'remove') {
    line.extras.splice(idx, 1);
  } else if (action === 'block') {
    showToast(`Höchstens ${rules.max} Extras möglich.`, 'error');
    return;
  } else {
    const e = findItem(extraId);
    if (!e) return;
    if (action === 'replace') line.extras = []; // Einfachauswahl
    line.extras.push({ id: e.id, name: e.name, price: Number(e.price) || 0 });
  }
  recalcLine(line);
  softRefresh();
}

/** Up-Sell: Beilage als Combo-Option an eine Burger-/Bowl-Bestellzeile
 *  hängen. S229 T1 — Checkbox-Semantik: mehrere VERSCHIEDENE Beilagen wählbar,
 *  je Beilage max 1× (zweiter Klick auf denselben Chip entfernt sie). Jede
 *  gewählte Beilage fließt in den Positionspreis. Die Beilagen-Dips
 *  (`sideSauces`) bleiben EINE Liste pro Position — sie werden erst geleert,
 *  wenn die LETZTE Beilage entfernt wurde. */
export function mpToggleSide(lineId, sideId) {
  const line = findLine(lineId);
  if (!line) return;
  const s = findItem(sideId);
  if (!s) return;
  line.sides = line.sides || [];
  const idx = line.sides.findIndex(x => x.id === sideId);
  if (idx >= 0) {
    line.sides.splice(idx, 1);
    if (line.sides.length === 0) line.sideSauces = [];   // keine Beilage mehr → Dips zurücksetzen
  } else {
    line.sides.push({ id: s.id, name: s.name, price: Number(s.price) || 0 });
  }
  recalcLine(line);
  softRefresh();
}

export function mpToggleSideSauce(lineId, sauceId) {
  const line = findLine(lineId);
  // S229 T1 — Guard „mindestens eine Beilage" statt Single-side.
  if (!line || !(Array.isArray(line.sides) && line.sides.length)) return;
  line.sideSauces = line.sideSauces || [];
  const idx = line.sideSauces.findIndex(e => e.id === sauceId);
  if (idx >= 0) {
    line.sideSauces.splice(idx, 1);
  } else {
    const e = findItem(sauceId);
    if (!e) return;
    line.sideSauces.push({ id: e.id, name: e.name, price: Number(e.price) || 0 });
  }
  recalcLine(line);
  softRefresh();
}

export function mpToggleSauce(lineId, sauceId) {
  const line = findLine(lineId);
  if (!line) return;
  line.sauces = line.sauces || [];
  const idx = line.sauces.findIndex(e => e.id === sauceId);
  if (idx >= 0) {
    line.sauces.splice(idx, 1);
  } else {
    const e = findItem(sauceId);
    if (!e) return;
    line.sauces.push({ id: e.id, name: e.name, price: Number(e.price) || 0 });
  }
  recalcLine(line);
  softRefresh();
}

export function mpSetSpecialRequests(v) {
  _state.specialRequests = v || '';
  // Kein Re-Render — textarea würde Cursor verlieren. Nur State-Update.
}
